import { useEffect, useRef } from 'react';
import { isPCAdapterActive } from 'lib/utils/pcAdapter';

type ShortcutKey = 'Enter' | 'Escape';

interface KeyboardShortcutOptions {
  onEnter?: () => void;
  onEscape?: () => void;
  enabled?: boolean;
  /**
   * 是否允许在 input / textarea / contenteditable 聚焦时继续响应 Enter。
   * 默认为 false，避免输入态误触发弹窗确认。
   * 仅在“输入框内按 Enter 就应该提交当前弹窗”的场景下显式开启。
   */
  allowEnterInInput?: boolean;
  /**
   * 阻断 Enter 继续向更早注册的弹窗传递。
   * 适用于当前层不处理 Enter，但也不希望下层弹窗响应该按键的场景。
   */
  blockEnter?: boolean;
  /**
   * 阻断 Escape 继续向更早注册的弹窗传递。
   * 适用于当前层不处理 Escape，但也不希望下层弹窗响应该按键的场景。
   */
  blockEscape?: boolean;
}

interface HandlerEntry {
  onEnter?: () => void;
  onEscape?: () => void;
  allowEnterInInput: boolean;
  blockEnter: boolean;
  blockEscape: boolean;
}

interface ShortcutResolveResult {
  handler?: () => void;
  blocked: boolean;
  allowEnterInInput: boolean;
}

/**
 * 栈式管理：
 * 1. 多层弹窗同时注册时，后挂载的 entry 优先级更高。
 * 2. 按键分发按“按键维度”从栈顶向下查找最近处理者，而不是整层独占。
 *    例如父层只处理 Escape、子层只处理 Enter 时，两个快捷键都能正常工作。
 * 3. 某一层若配置了 blockEnter / blockEscape，则会阻断对应按键继续向下传递。
 * 4. 弹窗卸载后自动出栈；当栈清空时自动移除全局监听，避免残留脏监听器。
 */
const handlerStack: HandlerEntry[] = [];
let listenerAttached = false;

/**
 * 输入类元素里按 Enter 往往有更高优先级的业务语义，
 * 这里直接跳过全局确认逻辑，避免误触发弹窗确认。
 */
function isTextInputActive(): boolean {
  const { activeElement } = document;

  if (!(activeElement instanceof HTMLElement)) return false;

  return activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.isContentEditable;
}

/**
 * 从栈顶向下按“某个按键”查找最近处理者。
 *
 * 规则如下：
 * 1. 当前层若提供了该按键的 handler，立即命中并停止向下查找。
 * 2. 当前层若没有 handler，但声明了 block，则停止继续向下传递。
 * 3. 当前层既没有 handler 也没有 block，则继续交给更早注册的层处理。
 */
function resolveShortcut(key: ShortcutKey): ShortcutResolveResult {
  for (let index = handlerStack.length - 1; index >= 0; index -= 1) {
    const entry = handlerStack[index];

    if (key === 'Enter') {
      if (entry.onEnter) {
        return { handler: entry.onEnter, blocked: true, allowEnterInInput: entry.allowEnterInInput };
      }

      if (entry.blockEnter) {
        return { blocked: true, allowEnterInInput: entry.allowEnterInInput };
      }

      continue;
    }

    if (entry.onEscape) {
      return { handler: entry.onEscape, blocked: true, allowEnterInInput: false };
    }

    if (entry.blockEscape) {
      return { blocked: true, allowEnterInInput: false };
    }
  }

  return { blocked: false, allowEnterInInput: false };
}

function globalKeyDownHandler(e: KeyboardEvent) {
  if (!isPCAdapterActive() || handlerStack.length === 0) return;

  switch (e.key) {
    case 'Enter': {
      const { handler, blocked, allowEnterInInput } = resolveShortcut('Enter');
      if (!blocked) return;

      if (isTextInputActive() && !allowEnterInInput) return;

      e.preventDefault();
      handler?.();
      break;
    }
    case 'Escape': {
      const { handler, blocked } = resolveShortcut('Escape');
      if (!blocked) return;

      e.preventDefault();
      handler?.();
      break;
    }
  }
}

function ensureGlobalListener() {
  if (!listenerAttached) {
    document.addEventListener('keydown', globalKeyDownHandler);
    listenerAttached = true;
  }
}

function cleanupGlobalListener() {
  if (listenerAttached && handlerStack.length === 0) {
    document.removeEventListener('keydown', globalKeyDownHandler);
    listenerAttached = false;
  }
}

/**
 * 键盘快捷键注册 Hook。
 *
 * 默认语义：
 * - 仅在 PC 适配器激活时生效。
 * - 仅在 enabled 为 true 时生效。
 * - 同一按键会从栈顶向下寻找最近处理者。
 * - 默认会跳过输入态下的 Enter，避免误触发弹窗确认；可通过 allowEnterInInput 显式放开。
 * - 若当前层未处理某个按键，可通过 blockEnter / blockEscape 阻止更下层继续响应。
 *
 * 推荐用法：
 * - 基础弹窗层注册通用的 Escape 关闭。
 * - 内容层在有自定义确认按钮时单独注册 Enter 确认。
 * - 只有在需要“当前层拦截但不处理”时，才配置 blockEnter / blockEscape。
 */
export function useKeyboardShortcut({
  onEnter,
  onEscape,
  enabled = true,
  allowEnterInInput = false,
  blockEnter = false,
  blockEscape = false,
}: KeyboardShortcutOptions): void {
  const entryRef = useRef<HandlerEntry>({ onEnter, onEscape, allowEnterInInput, blockEnter, blockEscape });

  entryRef.current.onEnter = onEnter;
  entryRef.current.onEscape = onEscape;
  entryRef.current.allowEnterInInput = allowEnterInInput;
  entryRef.current.blockEnter = blockEnter;
  entryRef.current.blockEscape = blockEscape;

  useEffect(() => {
    if (!isPCAdapterActive() || !enabled) return;

    ensureGlobalListener();

    const entry = entryRef.current;
    handlerStack.push(entry);

    return () => {
      const idx = handlerStack.indexOf(entry);
      if (idx !== -1) handlerStack.splice(idx, 1);
      cleanupGlobalListener();
    };
  }, [enabled]);
}
